import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useTimeZone } from '@/components/context/TimeZoneProvider';
import { parseTemplateVariableNames, replaceTemplateVariables, VariableStateMap } from '../../core';

export type TemplateVariableSrv = {
  state: VariableStateMap;
};

// Because we want template variable contexts to inherit any parent template variable context
// we are naming this internal and offering a separate provider / useContext hook that consumers
// must use instead of directly using Context.Provider
const InternalTemplateVariableContext = createContext<TemplateVariableSrv | undefined>(undefined);

export const TemplateVariableContextProvider = ({
  value,
  children,
}: {
  children: ReactNode;
  value: TemplateVariableSrv | undefined;
}) => {
  const parentCtx = useContext(InternalTemplateVariableContext);

  const combinedContext = useMemo(
    () => ({
      state: {
        ...(parentCtx?.state || {}),
        ...(value?.state || {}),
      },
    }),
    [parentCtx, value]
  );

  return (
    <InternalTemplateVariableContext.Provider value={combinedContext}>
      {children}
    </InternalTemplateVariableContext.Provider>
  );
};

function useTemplateVariableContext() {
  const ctx = useContext(InternalTemplateVariableContext);
  if (ctx === undefined) {
    throw new Error('No TemplateVariableContext found. Did you forget a Provider?');
  }
  return ctx;
}

export function useTemplateVariableValues(names?: string[]) {
  const { state } = useTemplateVariableContext();

  const values = useMemo(() => {
    const values: VariableStateMap = {};
    names?.forEach((name) => {
      const s = state[name];
      if (s) {
        values[name] = s;
      }
    });
    return values;
  }, [state, names]);

  if (names === undefined) {
    return state;
  }

  return values;
}

// Convenience hook for replacing template variables in a string
export function useReplaceVariablesInString(str: string | undefined): string | undefined {
  const { timeZone } = useTimeZone();
  const variableNames = str ? parseTemplateVariableNames(str) : [];
  const variableValues = useTemplateVariableValues(variableNames);
  if (!str) return undefined;
  return replaceTemplateVariables(str, variableValues, timeZone);
}
